import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { Calendar, Tag, ChevronRight, Search, Users, UserCheck, Clock } from 'lucide-react';

interface Candidate {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  stageTitle?: string;
  vacancyTitle?: string;
  vacancyId?: string;
  stage?: string;
  createdAt: string;
}

interface Vacancy { id: string; title: string; }
interface Stage { _id: string; title: string; }

const Candidates: React.FC = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filterVacancy, setFilterVacancy] = useState<string>('all');
  const [filterStage, setFilterStage] = useState<string>('all');
  const [allVacancies, setAllVacancies] = useState<Vacancy[]>([]);
  const [allStages, setAllStages] = useState<Stage[]>([]);

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const [candRes, stagesRes, vacanciesRes] = await Promise.all([
          api.get('/candidates'), api.get('/candidate-stages'), api.get('/vacancies')
        ]);
        const stages = stagesRes.data;
        const stageMap = new Map(stages.map((s: any) => [s._id, s.title]));
        setAllStages(stages.map((s: any) => ({ _id: s._id, title: s.title })));

        const vacancies = vacanciesRes.data;
        const vacancyMap = new Map<string, string>();
        vacancies.forEach((v: any) => {
          const vid = v.id || v._id;
          if (vid) vacancyMap.set(vid.toString(), v.title);
        });
        setAllVacancies(vacancies.map((v: any) => ({ id: v.id || v._id, title: v.title })));

        const data = candRes.data.map((c: any) => {
          const name = c.firstName && c.lastName ? `${c.firstName} ${c.lastName}`.trim() : c.name || 'Без имени';
          const vacId = typeof c.vacancyId === 'object' ? c.vacancyId?._id?.toString() : c.vacancyId?.toString();
          return {
            ...c, name,
            email: c.email || c.contactEmail || '',
            phone: c.phone || c.contactPhone || '',
            stageTitle: stageMap.get(c.stage) || '—',
            vacancyTitle: vacancyMap.get(vacId) || '—',
            vacancyId: vacId || '', stage: c.stage || '',
          };
        });
        setCandidates(data);
      } catch (err) {
        setError('Не удалось загрузить кандидатов.');
      } finally {
        setLoading(false);
      }
    };
    fetchCandidates();
  }, []);

  const filtered = useMemo(() => {
    let result = candidates;
    if (filterVacancy !== 'all') result = result.filter(c => c.vacancyId === filterVacancy);
    if (filterStage !== 'all') result = result.filter(c => c.stage === filterStage);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(q) || (c.email && c.email.toLowerCase().includes(q)));
    }
    return result;
  }, [candidates, filterVacancy, filterStage, search]);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка кандидатов...</div>;
  if (error) return <div style={{ padding: 60, textAlign: 'center', color: '#ef4444' }}><p>{error}</p></div>;

  const total = candidates.length;
  const inWork = candidates.filter(c => c.stageTitle && c.stageTitle !== '—').length;
  const uniqueStages = new Set(candidates.map(c => c.stage).filter(Boolean)).size;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
          <Users size={24} />
        </div>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Кандидаты</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Все резюме и статусы отбора</p>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Users size={22} /></div>
          <div><div style={styles.kpiLabel}>Всего кандидатов</div><div style={styles.kpiValue}>{total}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(106,17,203,0.1)', color: '#6a11cb' }}><UserCheck size={22} /></div>
          <div><div style={styles.kpiLabel}>В работе</div><div style={styles.kpiValue}>{inWork}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><Clock size={22} /></div>
          <div><div style={styles.kpiLabel}>Уникальных этапов</div><div style={styles.kpiValue}>{uniqueStages}</div></div>
        </div>
      </div>

      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 320 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input type="text" placeholder="Поиск по имени или email..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', padding: '10px 16px 10px 38px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(10px)', fontSize: 14, outline: 'none', color: '#1a1a2e' }} />
        </div>
        <select value={filterVacancy} onChange={e => setFilterVacancy(e.target.value)} style={styles.selectStyle}>
          <option value="all">Все вакансии</option>
          {allVacancies.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
        </select>
        <select value={filterStage} onChange={e => setFilterStage(e.target.value)} style={styles.selectStyle}>
          <option value="all">Все этапы</option>
          {allStages.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
        </select>
        {selected.size >= 2 && <button onClick={() => navigate(`/app/recruitment-analytics/candidates/compare?ids=${Array.from(selected).join(',')}`)} style={styles.compareBtn}>Сравнить ({selected.size})</button>}
      </div>

      {/* Таблица */}
      <div style={styles.glassCard}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...styles.th, display: 'none' }}></th>
                <th style={styles.th}>Кандидат</th>
                <th style={styles.th}>Вакансия</th>
                <th style={styles.th}>Этап</th>
                <th style={styles.th}>Дата</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Нет кандидатов</td></tr>
              ) : (
                filtered.map(c => (
                  <tr key={c._id} onClick={() => navigate(`/app/recruitment-analytics/candidates/${c._id}`)} style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,117,252,0.03)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...styles.td, display: 'none' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(c._id)} onChange={() => toggleSelect(c._id)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>{c.email || c.phone || ''}</div>
                    </td>
                    <td style={styles.td}>{c.vacancyTitle}</td>
                    <td style={styles.td}>
                      <span style={styles.stageBadge}><Tag size={14} /> {c.stageTitle}</span>
                    </td>
                    <td style={styles.td}>
                      <Calendar size={14} style={{ marginRight: 4, color: '#94a3b8' }} />
                      {new Date(c.createdAt).toLocaleDateString('ru-RU')}
                    </td>
                    <td style={styles.td}><ChevronRight size={16} color="#94a3b8" /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)', overflow: 'hidden' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  selectStyle: { padding: '10px 16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(10px)', fontSize: 14, outline: 'none', color: '#1a1a2e' },
  compareBtn: { background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', border: 'none', borderRadius: 24, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, boxShadow: '0 4px 12px rgba(37,117,252,0.3)' },
  th: { textAlign: 'left', padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  td: { padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.03)', fontSize: 14, color: '#1a1a2e' },
  stageBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 500, background: 'rgba(37,117,252,0.1)', color: '#2575fc' },
};

export default Candidates;
